import { Service } from '../../types';

interface SchemaGeneratorOptions {
  city?: string;
  state?: string;
  brand?: string;
  service?: Service;
}

export interface ServiceSchemaProps {
  name: string;
  description: string;
  location?: {
    city: string;
    state: string;
  };
  price?: {
    min: number;
    max: number;
  };
}

export function ServiceSchema({ name, description, location, price }: ServiceSchemaProps) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Parakh Services',
      telephone: '+918302311436',
      priceRange: price ? `₹${price.min} - ₹${price.max}` : undefined,
      address: location ? {
        '@type': 'PostalAddress',
        addressLocality: location.city,
        addressRegion: location.state,
        addressCountry: 'IN'
      } : undefined
    },
    areaServed: location ? {
      '@type': 'City',
      name: location.city
    } : undefined,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Service Packages',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Basic Service',
            description: `Basic ${name.toLowerCase()} inspection and diagnosis`
          },
          price: '499',
          priceCurrency: 'INR'
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Standard Service',
            description: `Complete ${name.toLowerCase()} service and repair`
          },
          price: '999',
          priceCurrency: 'INR'
        }
      ]
    }
  };
}

export function generateSchemas({
  city,
  state,
  brand,
  service
}: SchemaGeneratorOptions) {
  return {
    organization: generateOrganizationSchema(),
    localBusiness: generateLocalBusinessSchema({ city, state }),
    service: service ? ServiceSchema({
      name: service.name,
      description: service.description,
      location: city && state ? { city, state } : undefined
    }) : undefined,
    breadcrumb: generateBreadcrumbSchema({ service, city, state, brand })
  };
}

function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Parakh Services',
    url: 'https://parakhservices.com',
    logo: 'https://parakhservices.com/logo.png',
    sameAs: [
      'https://facebook.com/parakhservices',
      'https://twitter.com/parakhservices',
      'https://instagram.com/parakhservices'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+918302311436',
      contactType: 'customer service',
      areaServed: 'IN',
      availableLanguage: ['en', 'hi']
    }
  };
}

function generateLocalBusinessSchema({ city, state }: any) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Parakh Services',
    image: 'https://parakhservices.com/store-image.jpg',
    '@id': 'https://parakhservices.com',
    url: 'https://parakhservices.com',
    telephone: '+918302311436',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Service Center',
      addressLocality: city || '',
      addressRegion: state || '',
      addressCountry: 'IN'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '20.5937',
      longitude: '78.9629'
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ],
      opens: '00:00',
      closes: '23:59'
    }
  };
}

function generateBreadcrumbSchema({ service, city, state, brand }: any) {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://parakhservices.com'
    }
  ];

  if (service) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: 'Services',
      item: 'https://parakhservices.com/services'
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: service.name,
      item: `https://parakhservices.com/services/${service.id}`
    });
  }

  if (city && state) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: `${city}, ${state}`,
      item: `https://parakhservices.com/locations/${state}/${city}`
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items
  };
}